import { useState } from "react";
import { ChevronDown, ChevronRight, Mail, ShieldCheck, ClipboardCheck, PackageCheck,
  Receipt, Trash2, TrendingDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { opsApi } from "./lib/api";
import { GroupSendDialog, GroupQuoteDialog, GroupReceiveDialog,
  GroupAckDialog } from "./tabs/purchasing/GroupOrderDialogs";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2,
  maximumFractionDigits: 2 })}`;
const PILL = {
  draft: "bg-slate-100 text-slate-700", quote_requested: "bg-blue-100 text-blue-800",
  quoted: "bg-violet-100 text-violet-800", ordered: "bg-emerald-100 text-emerald-800",
  partially_received: "bg-amber-100 text-amber-800", received: "bg-emerald-100 text-emerald-800",
  closed: "bg-slate-100 text-slate-600", void: "bg-rose-100 text-rose-700",
};
const PRE_ORDER = ["draft", "quote_requested", "quoted"];
const PLACED = ["ordered", "partially_received"];

/** One live group order — 1785-N. The vendor sees one order; every line stays attributed to its
 *  own job, cost code and cost type, so the commitment and the saving land on the right job. */
export default function GroupOrderCard({ group, onChanged }) {
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState("");
  const [busy, setBusy] = useState(false);
  const g = group;

  const act = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); toast.success(ok(r)); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That did not go through"); }
    finally { setBusy(false); }
  };

  const placeOrder = () => {
    if (!window.confirm(`Place ${g.group_po_number} with ${g.manufacturer}? Every line commits `
      + "to its own job at the quoted cost.")) return;
    act(() => opsApi.groupOrderPlace(g.id, { authorization_note: g.authorization_note || "" }),
      () => `${g.group_po_number} placed — ${money(g.committed_total)} committed`);
  };

  const voidGroup = () => {
    if (!window.confirm(`${g.status === "draft" ? "Discard" : "Void"} ${g.group_po_number}? `
      + "Every line returns to the monitor. The number is never reused.")) return;
    act(() => opsApi.groupVoid(g.id),
      (r) => r.discarded ? `${r.discarded} discarded` : `${r.voided} voided`);
  };

  const removeJob = (job) => {
    if (!window.confirm(`Pull ${job.job_number} off ${g.group_po_number}? Its lines go back to `
      + "the monitor.")) return;
    act(() => opsApi.groupRemoveJob(g.id, job.project_id),
      () => `${job.job_number} is back in the monitor`);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white"
         data-testid={`group-order-${g.group_po_number}`}>
      <button onClick={() => setOpen((v) => !v)}
              data-testid={`group-order-toggle-${g.group_po_number}`}
              className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left">
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" />
          : <ChevronRight className="h-4 w-4 text-slate-400" />}
        <span className="font-mono text-sm font-semibold text-slate-900">
          {g.group_po_number}
        </span>
        <span className="text-sm text-slate-700">{g.manufacturer}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
          PILL[g.status] || "bg-slate-100 text-slate-700"}`}
              data-testid={`group-order-status-${g.group_po_number}`}>
          {g.status_label}
        </span>
        <span className="text-xs text-slate-500">
          {(g.jobs || []).length} jobs · {g.line_count} line(s)
        </span>
        {g.send_by && (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">
            send by {g.send_by}
          </span>
        )}
        {g.past_window_lines > 0 && (
          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] text-rose-700">
            {g.past_window_lines} past order-by
          </span>
        )}
        {g.saving > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
            <TrendingDown className="h-3 w-3" /> {money(g.saving)} saved ({g.saving_pct}%)
          </span>
        )}
        <span className="ml-auto text-sm font-semibold text-slate-900">
          {money(g.committed_total)}
        </span>
      </button>

      {open && (
        <div className="border-t border-slate-100 px-4 py-3">
          {g.authorization_note && (
            <p className="mb-2 rounded-md bg-amber-50 px-3 py-1.5 text-xs text-amber-800"
               data-testid={`group-order-auth-${g.group_po_number}`}>
              Bought on a job that is not Open, with written authorization:
              “{g.authorization_note}”
            </p>
          )}

          <table className="w-full text-xs" data-testid={`group-order-jobs-${g.group_po_number}`}>
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1.5 text-left">Job</th>
                <th className="px-2 py-1.5 text-right">Lines</th>
                <th className="px-2 py-1.5 text-right">Budgeted</th>
                <th className="px-2 py-1.5 text-right">Quoted</th>
                <th className="px-2 py-1.5 text-right">Freight</th>
                <th className="px-2 py-1.5 text-right">Tax</th>
                <th className="px-2 py-1.5 text-right">Saving</th>
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(g.jobs || []).map((j) => (
                <tr key={j.project_id} data-testid={`group-order-job-${j.job_number}`}>
                  <td className="px-2 py-1">
                    <span className="font-medium">{j.job_number}</span>
                    <span className="ml-1 text-slate-500">{j.name}</span>
                  </td>
                  <td className="px-2 py-1 text-right">{j.lines || 0}</td>
                  <td className="px-2 py-1 text-right">{money(j.budgeted)}</td>
                  <td className="px-2 py-1 text-right">{money(j.quoted)}</td>
                  <td className="px-2 py-1 text-right">{money(j.freight)}</td>
                  <td className="px-2 py-1 text-right">{money(j.tax)}</td>
                  <td className="px-2 py-1 text-right text-emerald-700">{money(j.saving)}</td>
                  <td className="px-2 py-1 text-right">
                    {PRE_ORDER.includes(g.status) && (g.jobs || []).length > 1 && (
                      <button onClick={() => removeJob(j)} disabled={busy}
                              data-testid={`group-order-remove-${j.job_number}`}
                              className="text-slate-400 hover:text-rose-700">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <table className="mt-3 w-full text-xs"
                 data-testid={`group-order-lines-${g.group_po_number}`}>
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1.5 text-left">Job</th>
                <th className="px-2 py-1.5 text-left">Part</th>
                <th className="px-2 py-1.5 text-left">Description</th>
                <th className="px-2 py-1.5 text-left">Cost code</th>
                <th className="px-2 py-1.5 text-right">Qty</th>
                <th className="px-2 py-1.5 text-right">Received</th>
                <th className="px-2 py-1.5 text-right">Budget unit</th>
                <th className="px-2 py-1.5 text-right">Quoted unit</th>
                <th className="px-2 py-1.5 text-right">Committed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {g.lines.map((l) => (
                <tr key={l.id} data-testid={`group-order-line-${l.id}`}>
                  <td className="px-2 py-1 text-slate-500">{l.job_number}</td>
                  <td className="px-2 py-1 font-mono">{l.part_number || "—"}</td>
                  <td className="px-2 py-1">
                    {l.description}
                    {l.source === "co" && <span className="ml-1 text-violet-700">· CO</span>}
                    {l.past_window && (
                      <span className="ml-1 text-rose-700">· past its order-by</span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-slate-500">
                    <span className="font-mono">{l.cost_code || "—"}</span>
                    {l.cost_type && <span className="ml-1">· {l.cost_type}</span>}
                  </td>
                  <td className="px-2 py-1 text-right">{Number(l.qty).toLocaleString()}</td>
                  <td className="px-2 py-1 text-right">
                    {l.received_qty ? Number(l.received_qty).toLocaleString() : "—"}
                  </td>
                  <td className="px-2 py-1 text-right text-slate-500">
                    {money(l.budgeted_unit_cost)}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {l.quoted_unit_cost ? money(l.quoted_unit_cost) : "—"}
                  </td>
                  <td className="px-2 py-1 text-right">{money(l.committed_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="mt-1.5 text-right text-sm text-slate-700"
             data-testid={`group-order-totals-${g.group_po_number}`}>
            Budgeted {money(g.budgeted_total)} · quoted {money(g.quoted_total)} · freight
            {" "}{money(g.freight_amount)} · tax {money(g.tax_amount)} ·
            <b className="ml-1 text-slate-900">committed {money(g.committed_total)}</b>
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {PRE_ORDER.includes(g.status) && (
              <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => setDialog("rfq")}
                      data-testid={`group-order-rfq-${g.group_po_number}`}>
                <Mail className="mr-1.5 h-3.5 w-3.5" /> Request a quote
              </Button>
            )}
            {PRE_ORDER.includes(g.status) && (
              <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => setDialog("quote")}
                      data-testid={`group-order-quote-${g.group_po_number}`}>
                <Receipt className="mr-1.5 h-3.5 w-3.5" /> Enter the quote
              </Button>
            )}
            {PRE_ORDER.includes(g.status) && (
              <Button size="sm" disabled={busy} onClick={placeOrder}
                      data-testid={`group-order-place-${g.group_po_number}`}>
                <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Place the order
              </Button>
            )}
            {["quote_requested", "quoted"].includes(g.status) && (
              <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => setDialog("order-email")}
                      data-testid={`group-order-send-${g.group_po_number}`}>
                <Mail className="mr-1.5 h-3.5 w-3.5" /> Order by email
              </Button>
            )}
            {PLACED.includes(g.status) && (
              <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => setDialog("ack")}
                      data-testid={`group-order-ack-${g.group_po_number}`}>
                <ClipboardCheck className="mr-1.5 h-3.5 w-3.5" /> Record acknowledgement
              </Button>
            )}
            {PLACED.includes(g.status) && (
              <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => setDialog("receive")}
                      data-testid={`group-order-receive-${g.group_po_number}`}>
                <PackageCheck className="mr-1.5 h-3.5 w-3.5" /> Receive
              </Button>
            )}
            {g.status !== "void" && !g.lines.some((l) => l.received_qty) && (
              <Button size="sm" variant="ghost" disabled={busy} onClick={voidGroup}
                      data-testid={`group-order-void-${g.group_po_number}`}
                      className="text-slate-500 hover:text-rose-700">
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                {g.status === "draft" ? "Discard" : "Void"}
              </Button>
            )}
            {(g.current_ship_date || g.vendor_order_number) && (
              <span className="ml-auto text-xs text-slate-500"
                    data-testid={`group-order-vendor-${g.group_po_number}`}>
                {g.vendor_order_number && `vendor order ${g.vendor_order_number}`}
                {g.current_ship_date && ` · ships ${g.current_ship_date.slice(0, 10)}`}
              </span>
            )}
          </div>
        </div>
      )}

      {(dialog === "rfq" || dialog === "order-email") && (
        <GroupSendDialog group={g} placeOrder={dialog === "order-email"}
                         onClose={() => setDialog("")}
                         onDone={() => { setDialog(""); onChanged?.(); }} />
      )}
      {dialog === "quote" && (
        <GroupQuoteDialog group={g} onClose={() => setDialog("")}
                          onDone={() => { setDialog(""); onChanged?.(); }} />
      )}
      {dialog === "ack" && (
        <GroupAckDialog group={g} onClose={() => setDialog("")}
                        onDone={() => { setDialog(""); onChanged?.(); }} />
      )}
      {dialog === "receive" && (
        <GroupReceiveDialog group={g} onClose={() => setDialog("")}
                            onDone={() => { setDialog(""); onChanged?.(); }} />
      )}
    </div>
  );
}
